import { createSelector, createSelectorCreator, defaultMemoize } from "reselect";
import { selectedContextSelector, primaryContextSelector } from "orion-components/ContextPanel/Selectors";
import { feedEntitiesGeoByTypeSelector } from "./actions";
import isEqual from "lodash/isEqual";
import groupBy from "lodash/groupBy";
import flatMap from "lodash/flatMap";
import { feedEntitiesSelectorUsingGlobalGeoData } from "../../GlobalData/Selectors";

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export const persistedState = createDeepEqualSelector(
	(state) => state.appState.persisted,
	(persisted) => persisted
);
export const profileState = (state) => state.appState.contextPanel.profile;
const contextualData = (state) => state.contextualData;
const primarySelector = (state) => state.contextualData[primaryContextSelector(state)];
const pinnedItemsSelector = (state) => primarySelector(state)?.pinnedItems ?? [];
const compositeDevicesSelector = (state) => state.globalData.compositeDevices;

export const mapSettingsSelector = createDeepEqualSelector(persistedState, (state) => {
	const settings = state.mapSettings || {};
	return {
		mapZoom: settings.mapZoom || 3,
		mapCenter: settings.mapCenter || [-98.2015, 39.4346],
		mapStyle: settings.mapStyle || "satellite",
		...settings
	};
});

export const widgetStateSelector = createSelector(
	persistedState,
	profileState,
	selectedContextSelector,
	(persisted, profile, context) => {
		let type = profile.selectedEntity.type;
		const isRobotTrack = context?.entity?.entityData?.properties?.type === "Robot Track";

		switch (type) {
			case "shapes":
			case "track":
				type = isRobotTrack ? "robot" : "entity";
				break;
			case "camera":
				break;
			case undefined: // this should be removed once events are given an entityType prop
				type = "event";
				break;
			default:
				break;
		}

		return persisted.profiles ? persisted.profiles[type] : null;
	}
);

export const disabledFeedsSelector = createSelector(persistedState, (state) =>
	state && state.disabledFeeds ? state.disabledFeeds : []
);

export const layerOpacitySelector = createSelector(persistedState, (state) =>
	state.layerOpacity ? state.layerOpacity : 1
);

const activityFilterState = (state) => state.appState?.persisted?.profiles;

export const activityFiltersSelector = createDeepEqualSelector(
	[activityFilterState, (state, profile) => profile],
	(state, profile) => {
		if (state?.[profile]?.widgets?.activities?.filters) {
			const { filters } = state[profile].widgets.activities;
			return filters.length > 0 ? filters : [];
		}
		return [];
	}
);

export const eventFiltersSelector = createSelector(persistedState, (state) =>
	state.eventFilters ? state.eventFilters : []
);

export const eventTemplateFiltersSelector = createSelector(persistedState, (state) =>
	state.eventTemplateFilters ? state.eventTemplateFilters : []
);

const feedEntitiesByType = (state, entityType) => {
	return feedEntitiesGeoByTypeSelector(entityType)(state);
};

// Returns all floorplan entities from active/enabled floorplans.
export const getFloorPlanEntitiesByType = () => {
	return createDeepEqualSelector(
		persistedState,
		contextualData,
		feedEntitiesByType,
		pinnedItemsSelector,
		(persisted, context, feedEntities, pinnedItems) => (selectedFloor) => {
			const selectedFloors = persisted?.selectedFloors || {};
			if (selectedFloor) selectedFloors[selectedFloor.facilityId] = selectedFloor;
			const filteredSelectedFloors = {};
			const pinnedItemsById = groupBy(pinnedItems, "id");
			if (pinnedItems.length && selectedFloors) {
				Object.keys(selectedFloors).forEach((facilityId) => {
					if (pinnedItemsById[facilityId] && selectedFloors[facilityId]) {
						filteredSelectedFloors[facilityId] = selectedFloors[facilityId];
					}
				});
			} else if (context && selectedFloors) {
				Object.keys(context).forEach((id) => {
					if (id === selectedFloors[id]?.facilityId) {
						filteredSelectedFloors[id] = selectedFloors[id];
					}
				});
			}

			const entities = Object.values(feedEntities);
			const floorIds = Object.keys(filteredSelectedFloors);

			const filteredEntities = floorIds?.map((facilityId) =>
				entities?.filter((entity) => {
					return (
						entity.entityData.displayType === "facility" &&
						entity.entityData.displayTargetId === filteredSelectedFloors[facilityId]?.id
					);
				})
			);

			return filteredEntities?.length ? flatMap(filteredEntities) : [];
		}
	);
};

export const sitePlanEntitiesSelector = () => {
	return createDeepEqualSelector(
		feedEntitiesSelectorUsingGlobalGeoData,
		compositeDevicesSelector,
		(feedEntities, compositeDevices) => (sitePlanId) => {
			const mergedEntities = { ...feedEntities, ...compositeDevices };
			const entities = Object.values(mergedEntities);
			const filteredEntities = entities?.filter((entity) => entity.entityData?.displayTargetId === sitePlanId);

			return filteredEntities;
		}
	);
};
